import React, { useEffect, useRef, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    List,
    ListItemButton,
    ListItemText,
    TextField,
} from '@mui/material';
import CancelIcon from '@mui/icons-material/Cancel';
import PlaceIcon from '@mui/icons-material/Place';
import { screenDialogBodySx } from './dialogSx';
import { loadGoogleMaps } from '../../Infralytiqs/Reports/googleMapsLoader';
import { useGoogleMapsApiKey } from '../../Infralytiqs/Reports/useGoogleMapsApiKey';

const LOCATION_MAX = 200;

export interface LatLng {
    lat: number;
    lng: number;
}

/** Reads the first latitude, longitude pair out of a stored location string. */
export const parseScreenLocation = (value: string): LatLng | null => {
    const match = value.match(/(-?\d+(?:\.\d+)?)\s*,\s*(-?\d+(?:\.\d+)?)/);
    if (!match) return null;
    const lat = Number(match[1]);
    const lng = Number(match[2]);
    if (!Number.isFinite(lat) || !Number.isFinite(lng) || lat < -90 || lat > 90 || lng < -180 || lng > 180) return null;
    return { lat, lng };
};

const formatPosition = (position: LatLng, placeName = ''): string => {
    const coords = `${position.lat.toFixed(6)}, ${position.lng.toFixed(6)}`;
    const text = placeName ? `${placeName} (${coords})` : coords;
    return text.length > LOCATION_MAX ? coords : text;
};

export interface ScreenLocationDialogProps {
    open: boolean;
    value: string;
    onClose: () => void;
    onSelect: (location: string) => void;
}

/**
 * Search, pan, and click a map to store where a screen is installed.
 * The chosen point is saved as latitude and longitude (with a place name when the map can resolve one).
 */
export const ScreenLocationDialog: React.FC<ScreenLocationDialogProps> = ({ open, value, onClose, onSelect }) => {
    const apiKey = useGoogleMapsApiKey();
    const mapHost = useRef<HTMLDivElement | null>(null);
    const mapRef = useRef<any>(null);
    const markerRef = useRef<any>(null);
    const mapsRef = useRef<any>(null);
    const [mapReady, setMapReady] = useState(false);
    const [query, setQuery] = useState('');
    const [results, setResults] = useState<Array<{ label: string; position: LatLng }>>([]);
    const [position, setPosition] = useState<LatLng | null>(null);
    const [error, setError] = useState('');
    const [manualLat, setManualLat] = useState('');
    const [manualLng, setManualLng] = useState('');

    useEffect(() => {
        if (!open) {
            setMapReady(false);
            return;
        }
        const parsed = parseScreenLocation(value);
        setPosition(parsed);
        setManualLat(parsed ? String(parsed.lat) : '');
        setManualLng(parsed ? String(parsed.lng) : '');
        setQuery('');
        setResults([]);
        setError('');
        setMapReady(false);
    }, [open, value]);

    useEffect(() => {
        if (!open || !mapReady || !apiKey || !mapHost.current) return undefined;
        let cancelled = false;
        const host = mapHost.current;
        loadGoogleMaps(apiKey)
            .then((maps) => {
                if (cancelled) return;
                mapsRef.current = maps;
                const start = position || { lat: 51.5074, lng: -0.1278 };
                const map = new maps.Map(host, {
                    center: start,
                    zoom: position ? 15 : 4,
                    mapTypeControl: false,
                    streetViewControl: false,
                    fullscreenControl: false,
                });
                mapRef.current = map;
                if (position) markerRef.current = new maps.Marker({ map, position });
                map.addListener('click', (event: { latLng?: { lat: () => number; lng: () => number } }) => {
                    if (!event.latLng) return;
                    const next = { lat: event.latLng.lat(), lng: event.latLng.lng() };
                    if (!markerRef.current) markerRef.current = new maps.Marker({ map });
                    markerRef.current.setPosition(next);
                    setPosition(next);
                    setManualLat(next.lat.toFixed(6));
                    setManualLng(next.lng.toFixed(6));
                });
            })
            .catch((err) => {
                if (!cancelled) setError(err instanceof Error ? err.message : 'Could not load the map');
            });
        return () => {
            cancelled = true;
            const maps = mapsRef.current;
            if (maps && mapRef.current) maps.event?.clearInstanceListeners(mapRef.current);
            mapRef.current = null;
            markerRef.current = null;
        };
        // Recreate the map when the dialog finishes opening. The chosen pin is applied by the click handler.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, mapReady, apiKey]);

    const placePin = (next: LatLng, zoom = 15) => {
        const maps = mapsRef.current;
        const map = mapRef.current;
        setPosition(next);
        setManualLat(next.lat.toFixed(6));
        setManualLng(next.lng.toFixed(6));
        if (!maps || !map) return;
        if (!markerRef.current) markerRef.current = new maps.Marker({ map });
        markerRef.current.setPosition(next);
        map.panTo(next);
        map.setZoom(zoom);
    };

    const search = async () => {
        const text = query.trim();
        if (!text) return;
        setError('');
        try {
            const res = await fetch(`https://nominatim.openstreetmap.org/search?format=jsonv2&limit=5&q=${encodeURIComponent(text)}`);
            if (!res.ok) throw new Error('Search failed');
            const found = (await res.json()) as Array<{ display_name?: string; lat?: string; lon?: string }>;
            const next = found
                .map((item) => ({
                    label: item.display_name || text,
                    position: { lat: Number(item.lat), lng: Number(item.lon) },
                }))
                .filter((item) => Number.isFinite(item.position.lat) && Number.isFinite(item.position.lng));
            if (!next.length) {
                setResults([]);
                setError('No places matched that search');
                return;
            }
            setResults(next);
            placePin(next[0].position);
        } catch {
            setError('Could not search for that place');
        }
    };

    const applyManual = () => {
        const lat = Number(manualLat);
        const lng = Number(manualLng);
        if (!Number.isFinite(lat) || !Number.isFinite(lng) || lat < -90 || lat > 90 || lng < -180 || lng > 180) {
            setError('Enter a latitude between -90 and 90 and a longitude between -180 and 180');
            return;
        }
        setError('');
        placePin({ lat, lng });
    };

    const confirm = () => {
        const chosen = position || (Number.isFinite(Number(manualLat)) && Number.isFinite(Number(manualLng)) ? { lat: Number(manualLat), lng: Number(manualLng) } : null);
        if (!chosen) return;
        void fetch(`https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${chosen.lat}&lon=${chosen.lng}`)
            .then((res) => (res.ok ? res.json() : null))
            .then((data: { display_name?: string } | null) => {
                onSelect(formatPosition(chosen, data?.display_name || ''));
                onClose();
            })
            .catch(() => {
                onSelect(formatPosition(chosen));
                onClose();
            });
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="md" TransitionProps={{ onEntered: () => setMapReady(true) }}>
            <DialogTitle>Screen location</DialogTitle>
            <DialogContent sx={screenDialogBodySx}>
                {!apiKey && <Alert severity="warning" sx={{ mb: 2 }}>The map is unavailable because no Google Maps key is configured. Enter the latitude and longitude below.</Alert>}
                {error && <Alert severity="warning" sx={{ mb: 2 }}>{error}</Alert>}
                <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                    <TextField
                        label="Search"
                        size="small"
                        fullWidth
                        value={query}
                        placeholder="Search for a place"
                        onChange={(e) => setQuery(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                                void search();
                            }
                        }}
                    />
                    <Button variant="contained" color="secondary" onClick={() => void search()} disabled={!query.trim()}>
                        Search
                    </Button>
                </Box>
                {results.length > 1 && (
                    <List dense sx={{ maxHeight: 140, overflow: 'auto', mb: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                        {results.map((item) => (
                            <ListItemButton key={item.label} onClick={() => placePin(item.position)}>
                                <ListItemText primary={item.label} />
                            </ListItemButton>
                        ))}
                    </List>
                )}
                <Box ref={mapHost} sx={{ height: apiKey ? 380 : 0, borderRadius: 1, overflow: 'hidden', bgcolor: 'action.hover' }} />
                <Box sx={{ display: 'flex', gap: 1, mt: 1.5 }}>
                    <TextField label="Latitude" size="small" value={manualLat} onChange={(e) => setManualLat(e.target.value)} fullWidth />
                    <TextField label="Longitude" size="small" value={manualLng} onChange={(e) => setManualLng(e.target.value)} fullWidth />
                    <Button variant="outlined" color="secondary" onClick={applyManual} sx={{ flexShrink: 0 }}>
                        Set
                    </Button>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button variant="outlined" color="secondary" startIcon={<CancelIcon />} onClick={onClose}>
                    Cancel
                </Button>
                <Button variant="contained" color="success" startIcon={<PlaceIcon />} disabled={!position && !manualLat.trim()} onClick={confirm}>
                    Use this location
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ScreenLocationDialog;

/** Read-only map for a screen that already has a saved latitude and longitude. */
export const ScreenLocationMap: React.FC<{ position: LatLng; fill?: boolean }> = ({ position, fill = false }) => {
    const apiKey = useGoogleMapsApiKey();
    const host = useRef<HTMLDivElement | null>(null);
    const [error, setError] = useState('');

    useEffect(() => {
        if (!apiKey || !host.current) return undefined;
        let cancelled = false;
        let map: { setCenter: (center: LatLng) => void } | null = null;
        let observer: ResizeObserver | null = null;
        const mapsHolder: { maps?: { event?: { trigger: (target: unknown, name: string) => void; clearInstanceListeners: (target: unknown) => void } } } = {};
        const node = host.current;
        loadGoogleMaps(apiKey)
            .then((maps) => {
                if (cancelled || !host.current) return;
                mapsHolder.maps = maps;
                map = new maps.Map(host.current, {
                    center: position,
                    zoom: 15,
                    mapTypeControl: false,
                    streetViewControl: false,
                    fullscreenControl: false,
                });
                new maps.Marker({ map, position });
                observer = new ResizeObserver(() => {
                    maps.event?.trigger(map, 'resize');
                    map?.setCenter(position);
                });
                observer.observe(node);
            })
            .catch((err) => {
                if (!cancelled) setError(err instanceof Error ? err.message : 'Could not load the map');
            });
        return () => {
            cancelled = true;
            observer?.disconnect();
            if (map) mapsHolder.maps?.event?.clearInstanceListeners(map);
        };
    }, [apiKey, position.lat, position.lng]);

    if (!apiKey) {
        return <Alert severity="warning">The map is unavailable because no Google Maps key is configured.</Alert>;
    }
    return (
        <Box sx={{ flex: 1, width: '100%', height: fill ? '100%' : undefined, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
            {error && <Alert severity="warning" sx={{ mb: 1 }}>{error}</Alert>}
            <Box ref={host} sx={{ flex: 1, width: '100%', height: fill ? '100%' : 360, minHeight: fill ? 280 : undefined, borderRadius: 1, overflow: 'hidden', bgcolor: 'action.hover' }} />
        </Box>
    );
};
